import firebase from "firebase/app";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { UserDataStorageBuilder } from "@lifeat/shared/firebase/storage/UserDataStorageBuilder";
import { USERS_NODE } from "@lifeat/shared/rtdb-nodes/RtdbNodes";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { getUserAccountCreationDateYYYYMMDD } from "@lifeat/shared/user/UserUtil";
import * as ArgumentUtil from "@lifeat/shared/util/ArgumentUtil";
import * as StringUtil from "@lifeat/shared/util/StringUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { db, storage } from "@firebase-local/firebase";
import { PublicUserInfo } from "../../room/RoomTypes";

const amplitudeService = AmplitudeService.getInstance();

/**
 * Send a verification email to the user who signed up with email/password combo.
 * Note: if a user is on the desktop app and this is not provided, we will still
 * include the query parameter to take the user to the application. This will
 * be used to trigger the desktop app to open. @see {@link EmailVerificationLandingPage} for more details
 * @param user The Firebase User.
 * @param redirectSuccessRoute Optional, route to redirect the user to after they
 * verify their email.
 */
export const sendUserVerificationEmail = async (user: firebase.User, redirectSuccessRoute?: string) => {
    if (!user.emailVerified) {
        const queryParams = new URLSearchParams();
        queryParams.append("email", user.email);
        if (StringUtil.isValidString(redirectSuccessRoute)) {
            queryParams.append(QueryParameters.RedirectSuccess, encodeURIComponent(redirectSuccessRoute));
        } else if (!!window.todesktop) {
            // If user is on desktop app, we include the redirectSuccess param to indicate
            // that they should be given the option to open the desktop app. This is handled
            // in EmailVerificationLandingPage.tsx. Because this redirect is happening right
            // after account creation, we take them to the onboarding page.
            queryParams.append(QueryParameters.RedirectSuccess, encodeURIComponent(`/${Routes.Signup}`));
        }
        const actionCodeSettings = {
            // NOTE: we include /app route because without it, the user will be
            //  taken to the reverse-proxied landing page, which then redirects
            //  them back to LifeAt, without any query parameters.
            url: `${window.location.origin}/${Routes.App}?${queryParams}`,
        };
        return user.sendEmailVerification(actionCodeSettings);
    }
};

/**
 * Check the DB for data for the given user as part of a sign in request.
 * If data exists, it means it has been initialized. Otherwise, needs to be initialized.
 * @param uid UID for the user logged in (from firebase.User)
 * @param handleLoginCallback Callback to perform based on whether or not the data exists.
 */
export const signUserInSuccessWithAuthResult = (uid: string, handleLoginCallback: (initData: boolean) => void) => {
    const userRef = db.ref(`${USERS_NODE.name}/${uid}`);
    // Reads data at a path and listen for changes
    return userRef.once("value", (snapshot) => {
        if (snapshot.exists()) {
            handleLoginCallback(false);
        } else {
            handleLoginCallback(true);
        }
    });
};
//// END: User Auth Functions ////

//// START: User Data Functions ////
/**
 * Update a user's data.
 * @param {string} uid User's UID.
 * @param {UserEntity} updatedData An up-to-date user entity object.
 * NOTE: this may cause race conditions in the future. TODO: fix this function
 *  to only update certain fields at a time instead of all.
 * @deprecated DO NOT USE
 */
export const updateUserData = (uid: string, updatedData: UserEntity): Promise<any> => {
    const userPath = `/${USERS_NODE.name}/${uid}`;
    const updates: { [id: string]: UserEntity } = {};
    updates[userPath] = updatedData;

    return db.ref(userPath).update(updatedData);
};

/**
 * On account creation the user's display name is not passed to the
 * Auth#onCreate serverless function, but is initialized client side.
 * See UserAccountFunctions#onAuthRecordCreate for more context.
 * @param user Firebase User
 */
export const initUserDisplayNameInDbIfMissing = (user: firebase.User) => {
    const nameToUse = user.displayName ?? "LifeAt User";
    db.ref(`${USERS_NODE.name}/${user.uid}/${USERS_NODE.children.DISPLAY_NAME}`).set(nameToUse);
};

/**
 * Update user's display name in Auth and, optionally, in the database.
 * @param {firebase.User} user Firebase User
 * @param {string} newDisplayName New display name for the user, greater than 4 characters, fewer than 32 characters.
 * @param {boolean} updateDb `true`, if the database entry for the user should be updated, else `false`.
 */
export const updateUserDisplayName = (user: firebase.User, newDisplayName: string, updateDb: boolean) => {
    if (user.displayName !== newDisplayName) {
        const uid = user.uid;
        user.updateProfile({
            displayName: newDisplayName,
        }).then(() => {
            if (updateDb) {
                const path = `/${USERS_NODE.name}/${uid}/${USERS_NODE.children.DISPLAY_NAME}`;
                const updates: { [id: string]: string } = {};
                updates[path] = newDisplayName;
                return db.ref().update(updates);
            }
            return undefined;
        });
    }
};

const USER_AVI_IMG_FILE_NAME = "user-avi-img";

/**
 * Update a user's custom profile image by storing the image and updating their
 * database reference to that hosted image's URL.
 * @param uid
 * @param imgFile
 * @param callback
 */
export const updateUserAviImg = (uid: string, imgFile: File, callback: () => void) => {
    const storagePath = new UserDataStorageBuilder(uid);

    const storageRef = storage.ref();
    const aviImagesRef = storageRef.child(storagePath.getPath()).child(`${USER_AVI_IMG_FILE_NAME}`);

    aviImagesRef
        .put(imgFile)
        .then((snapshot) => {
            // On successful upload, write the new URL to DB.
            snapshot.ref.getDownloadURL().then((url: string) => {
                db.ref(`/${USERS_NODE.name}/${uid}/${USERS_NODE.children.CUSTOM_AVI_URL}`).set(url).then(callback);
            });
        })
        .catch((e) => {
            // Unable to upload the file. Show alert to user.
            const msg = `Unable to upload a new profile image. Please try again.\n\nError Reason: ${e.message}`;
            alert(msg);
        });
};

//// END: User Data Functions ////

export async function getUserDisplayName(uid: string): Promise<string> {
    ArgumentUtil.validateUid(uid);

    const displayNamePath = `/${USERS_NODE.name}/${uid}/${USERS_NODE.children.DISPLAY_NAME}`;

    let displayName = "";
    await db.ref(displayNamePath).once("value", (snap) => {
        if (snap.exists()) {
            displayName = snap.val() as string;
        }
    });
    return displayName;
}

export async function getUserAviUrl(uid: string): Promise<string> {
    ArgumentUtil.validateUid(uid);

    const customAviUrlPath = `/${USERS_NODE.name}/${uid}/${USERS_NODE.children.CUSTOM_AVI_URL}`;

    let aviUrl = "";
    await db.ref(customAviUrlPath).once("value", (snap) => {
        if (snap.exists()) {
            aviUrl = snap.val() as string;
        }
    });
    return aviUrl;
}

/**
 * Returns display name and avi url for a given UID
 * @param uid User ID
 */
export const getPublicUserInfo = async (uid: string): Promise<PublicUserInfo> => {
    const displayName = await getUserDisplayName(uid);
    const aviUrl = await getUserAviUrl(uid);
    return {
        uid: uid,
        userDisplayName: displayName,
        userAviUrl: aviUrl,
    };
};

/**
 * Set amplitude user properties for email and account creation date
 * Note: This will only set the property if it doesn't already exist for the user
 * @param email User's email
 * @param accountCreationDate Account creation timestamp
 */
export const setAmplitudeUserAccountProperties = (
    email: string | undefined | null,
    accountCreationDate: string | undefined | null,
) => {
    if (email) {
        amplitudeService.setUserPropOnce(AmplitudeUserProperties.USER_EMAIL, email);
    }
    if (accountCreationDate) {
        amplitudeService.setUserPropOnce(
            AmplitudeUserProperties.ACCOUNT_CREATION_DATE,
            getUserAccountCreationDateYYYYMMDD(accountCreationDate),
        );
    }
};
